import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function OpportunityPerformanceTable({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  clients,
  employees,
  projecttypes,
  projectsubtypes,
  selectedCompany,
  selectedOrganisation,
  selectedDates,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
  getClients,
  getEmployees,
  getProjectTypes,
  getProjectSubTypes,
}) {
  const [opportunities, setOpportunities] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimOpportunityKey: 'All',
    DimDateKey: 'All',
    DimClientKey: 'All',
    DimEmployeeDirectoryKey: 'All',
    DimEmployeeProjectManagerKey: 'All',
    DimEmployeeSupervisorKey: 'All',
    DimEmployeeOpportunitySourceOneKey: 'All',
    DimEmployeeOpportunitySourceTwoKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    Status: '',
    Revenue: '',
    ProbabilityOfSuccess: '',
    PotentialRevenue: '',
    OpportunityType: 'All',
    OpportunitySubType: 'All',
    OpenedDate: '',
    ClosedDate: '',
    ReasonClosed: '',
    TotalHours: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'TotalHours':
      case 'Revenue':
      case 'ProbabilityOfSuccess':
      case 'PotentialRevenue':
        return (a, b) => {
          return (a[sortColumn] || 0) - (b[sortColumn] || 0);
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOpportunityKey':
        return (a, b) => {
          const aName = getOpportunityNameById(a[sortColumn]);
          const bName = getOpportunityNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimClientKey':
        return (a, b) => {
          const aName = getClientNameById(a[sortColumn]);
          const bName = getClientNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimEmployeeDirectoryKey':
      case 'DimEmployeeProjectManagerKey':
      case 'DimEmployeeSupervisorKey':
      case 'DimEmployeeOpportunitySourceOneKey':
      case 'DimEmployeeOpportunitySourceTwoKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    const asyncFn = () => {
      getOpportunities();
    };

    asyncFn();
  }, []);

  const getOpportunities = () => {
    ManageReportDataApi.getOpportunities()
      .then((res) => {
        if (res.status === 200) setOpportunities(res.data);
        else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.sort((a, b) => b.DimDateKey - a.DimDateKey);
    setTableRows(filterRows);
  }, [rows, activeFilter, companies, selectedCompany]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const getOpportunityIdById = (id) => {
    if (!id) return '';
    const opportunity = opportunities?.find(opportunity => opportunity[0] === id);
    return opportunity ? opportunity[0] : '';
  };

  const getOpportunityNameById = (id) => {
    if (!id) return '';
    const opportunity = opportunities?.find(opportunity => opportunity[0] === id);
    return opportunity ? opportunity[1] : '';
  };

  const getClientNameById = (id) => {
    if (!id) return '';
    const client = clients?.find(client => client[0] === id);
    return client ? client[1] : '';
  };

  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(employee => employee[0] === id);
    return employee ? employee[1] : '';
  };

  const getOpportunityTypeNameById = (id) => {
    if (!id) return '';
    const opportunityType = projecttypes?.find(opportunityType => opportunityType[0] === id);
    return opportunityType ? opportunityType[1] : '';
  };

  const getOpportunitySubTypeNameById = (id) => {
    if (!id) return '';
    const opportunitySubType = projectsubtypes?.find(opportunitySubType => opportunitySubType[0] === id);
    return opportunitySubType ? opportunitySubType[1] : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimOpportunityKey',
      'DimDateKey',
      'DimClientKey',
      'DimEmployeeDirectoryKey',
      'DimEmployeeProjectManagerKey',
      'DimEmployeeSupervisorKey',
      'DimEmployeeOpportunitySourceOneKey',
      'DimEmployeeOpportunitySourceTwoKey',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    const headerAliases = {
      'Opportunity Id': 'OpportunityId',
      'OpportunityID': 'OpportunityId',
      'Project Manager': 'Project Manager',
      'ProjectManager': 'Project Manager',
      'SourceOne': 'Source One',
      'Source Two': 'Source Two',
      'SourceTwo': 'Source Two',
      'Total Hours': 'TotalHours',
      'Potential Revenue': 'PotentialRevenue',
      'Probability Of Success': 'ProbabilityOfSuccess',
      'Weighted Potential Revenue': 'WeightedPotentialRevenue',
      'Reason Closed': 'ReasonClosed',
      'Opportunity Type': 'OpportunityType',
      'Opportunity SubType': 'OpportunitySubType',
      'Opened Date': 'OpenedDate',
      'Closed Date': 'ClosedDate',
    };

    const normalizedData = (updated_data || []).map((item) => {
      const row = { ...item };
      const valuesByTrimmed = {};
      Object.keys(row).forEach((k) => {
        const trimmed = (k || '').trim();
        if (trimmed) valuesByTrimmed[trimmed] = row[k];
      });

      Object.entries(headerAliases).forEach(([alias, canonical]) => {
        if (row[canonical] !== undefined) return;
        if (valuesByTrimmed[alias] !== undefined) row[canonical] = valuesByTrimmed[alias];
      });

      return row;
    });

    if (!selectedCompany || !selectedOrganisation || !selectedDates || selectedDates.length === 0) {
      toast.warning('Please select Company, Organisation, and DimDateKey at the top before importing.');
      return;
    }

    const validData = normalizedData.length > 0 && normalizedData.every((item) => {
      return Object.prototype.hasOwnProperty.call(item, 'OpportunityId')
        && Object.prototype.hasOwnProperty.call(item, 'Name');
    });

    if (!validData) {
      toast.warning('Please insert valid data. Data must contain OpportunityId and Name. Company, Organisation, and DimDateKey come from the top filters.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      normalizedData,
      selectedDates,
      selectedCompany,
      selectedOrganisation,
    ).then((res) => {
      if (res.status === 200) {
        getClients();
        getEmployees();
        getProjectTypes();
        getProjectSubTypes();
        getOpportunities();
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    const rowIndex = field.indexes[0];
    const row = updatedRows[rowIndex];

    let updated_data = {
      rowIds: [row.id],
      updated: {
        [field.column.key]: row[field.column.key],
      },
    };

    if (
      updated_data.updated[field.column.key] === '' &&
      !['Status', 'ReasonClosed', 'ClosedDate'].includes(field.column.key)
    ) {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      ['TotalHours', 'Revenue', 'ProbabilityOfSuccess', 'PotentialRevenue'].includes(
        field.column.key,
      )
    ) {
      if (
        updated_data.updated[field.column.key] !== '' &&
        isNaN(updated_data.updated[field.column.key])
      ) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (
      field.column.key === 'Revenue' ||
      field.column.key === 'ProbabilityOfSuccess'
    ) {
      const revenue = Number(row.Revenue || 0);
      const probability = Number(row.ProbabilityOfSuccess || 0);
      const potentialRevenue = Number(
        ((revenue * probability) / 100).toFixed(2),
      );

      row.PotentialRevenue = potentialRevenue;
      updated_data.updated.Revenue = row.Revenue;
      updated_data.updated.ProbabilityOfSuccess = row.ProbabilityOfSuccess;
      updated_data.updated.PotentialRevenue = potentialRevenue;
    }

    if (field.column.key === 'DimOpportunityKey') {
      let updated = {};
      let findOpportunity = opportunities.find(
        (opportunity) => opportunity[0] === row[field.column.key],
      );

      updated['DimOpportunityKey'] = findOpportunity[0];
      opportunities &&
        opportunities.forEach((cur) => {
          if (cur[0] === findOpportunity[0]) {
            updated['DimClientKey'] = cur[2];
            updated['DimEmployeeDirectoryKey'] = cur[3];
            updated['DimEmployeeProjectManagerKey'] = cur[4];
            updated['DimEmployeeSupervisorKey'] = cur[5];
            updated['DimEmployeeOpportunitySourceOneKey'] = cur[6];
            updated['DimEmployeeOpportunitySourceTwoKey'] = cur[7];
            updated['OpportunityType'] = cur[8];
            updated['OpportunitySubType'] = cur[9];
            updated['OpenedDate'] = cur[10];
            updated['ClosedDate'] = cur[11];

            updatedRows[rowIndex]['DimClientKey'] = cur[2];
            updatedRows[rowIndex]['DimEmployeeDirectoryKey'] = cur[3];
            updatedRows[rowIndex]['DimEmployeeProjectManagerKey'] = cur[4];
            updatedRows[rowIndex]['DimEmployeeSupervisorKey'] = cur[5];
            updatedRows[rowIndex]['DimEmployeeOpportunitySourceOneKey'] = cur[6];
            updatedRows[rowIndex]['DimEmployeeOpportunitySourceTwoKey'] = cur[7];
            updatedRows[rowIndex]['OpportunityType'] = cur[8];
            updatedRows[rowIndex]['OpportunitySubType'] = cur[9];
            updatedRows[rowIndex]['OpenedDate'] = cur[10];
            updatedRows[rowIndex]['ClosedDate'] = cur[11];
          }
        });

      updated_data.updated = Object.assign({}, updated);
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [...updated_data.rowIds, ...Array.from(selectedIds)];
    }

    updatedRows.map((curRow) => {
      if (updated_data.rowIds.includes(curRow.id)) {
        Object.keys(updated_data.updated).forEach((key) => {
          curRow[key] = updated_data.updated[key];
        });
      }
      return curRow;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };
  
  const opportunityPerformanceColumns = useMemo(() => {
    let opportunity_performance_columns = [
      SelectColumn,
      {
        key: 'DimOpportunityKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Opportunity</b>
            {frozenColumns.indexOf('DimOpportunityKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOpportunityKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOpportunityKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOpportunityKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOpportunityKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOpportunityKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOpportunityKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOpportunityKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {opportunities &&
                  opportunities.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOpportunityKey ? props.row.DimOpportunityKey : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimOpportunityKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {opportunities &&
                opportunities.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        resizable: true,
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimClientKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client</b>
            {frozenColumns.indexOf('DimClientKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimClientKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimClientKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimClientKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimClientKey ? props.row.DimClientKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimClientKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {clients &&
                clients.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeDirectoryKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Employee</b>
            {frozenColumns.indexOf('DimEmployeeDirectoryKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectoryKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectoryKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeDirectoryKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeDirectoryKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeDirectoryKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeDirectoryKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeDirectoryKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeDirectoryKey
                  ? props.row.DimEmployeeDirectoryKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeDirectoryKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectManagerKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Manager</b>
            {frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectManagerKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeProjectManagerKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectManagerKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectManagerKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectManagerKey
                  ? props.row.DimEmployeeProjectManagerKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectManagerKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeSupervisorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Associate</b>
            {frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeSupervisorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeSupervisorKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeSupervisorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeSupervisorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeSupervisorKey
                  ? props.row.DimEmployeeSupervisorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeSupervisorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeOpportunitySourceOneKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 1</b>
            {frozenColumns.indexOf('DimEmployeeOpportunitySourceOneKey') !==
            -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceOneKey',
                  )
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceOneKey',
                  )
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeOpportunitySourceOneKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeOpportunitySourceOneKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeOpportunitySourceOneKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeOpportunitySourceOneKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeOpportunitySourceOneKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeOpportunitySourceOneKey
                  ? props.row.DimEmployeeOpportunitySourceOneKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeOpportunitySourceOneKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeOpportunitySourceTwoKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 2</b>
            {frozenColumns.indexOf('DimEmployeeOpportunitySourceTwoKey') !==
            -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceTwoKey',
                  )
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceTwoKey',
                  )
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeOpportunitySourceTwoKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeOpportunitySourceTwoKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeOpportunitySourceTwoKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeOpportunitySourceTwoKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeOpportunitySourceTwoKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeOpportunitySourceTwoKey
                  ? props.row.DimEmployeeOpportunitySourceTwoKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeOpportunitySourceTwoKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      
{
        key: 'Status',
        name: 'Status',
        width: 130,
        resizable: true,
        sortable: true,
        frozen: false,
        renderEditCell: (props) => (
          <select
            className="rdg-select-editor"
            value={props.row.Status || ''}
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Status: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
            onKeyDown={selectStopPropagation}
          >
            <option value="" />
            <option value="INPROGRESS">INPROGRESS</option>
            <option value="WON">WON</option>
            <option value="LOST">LOST</option>
            <option value="CLOSED">CLOSED</option>
          </select>
        ),
      },
      
{
        key: 'Revenue',
        name: 'Revenue',
        width: 130,
        resizable: true,
        sortable: true,
        frozen: false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Revenue ?? ''}
            type="number"
            onChange={(event) => {
              const revenue = event.target.value;
              const probability = Number(props.row.ProbabilityOfSuccess || 0);
              const potentialRevenue =
                revenue === ''
                  ? ''
                  : Number(((Number(revenue || 0) * probability) / 100).toFixed(2));
              props.onRowChange({
                ...props.row,
                Revenue: revenue,
                PotentialRevenue: potentialRevenue,
              });
            }}
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      
{
        key: 'ProbabilityOfSuccess',
        name: 'Probability',
        width: 130,
        resizable: true,
        sortable: true,
        frozen: false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ProbabilityOfSuccess ?? ''}
            type="number"
            onChange={(event) => {
              const probability = event.target.value;
              const revenue = Number(props.row.Revenue || 0);
              const potentialRevenue =
                probability === ''
                  ? ''
                  : Number(((revenue * Number(probability || 0)) / 100).toFixed(2));
              props.onRowChange({
                ...props.row,
                ProbabilityOfSuccess: probability,
                PotentialRevenue: potentialRevenue,
              });
            }}
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      
{
        key: 'PotentialRevenue',
        name: 'Potential Revenue',
        width: 150,
        resizable: true,
        sortable: true,
        frozen: false,
      },
      {
        key: 'OpportunityType',
        name: 'Type',
        width: 120,
        resizable: true,
        sortable: true,
      },
      {
        key: 'OpportunitySubType',
        name: 'SubType',
        width: 120,
        resizable: true,
        sortable: true,
      },
      {
        key: 'OpenedDate',
        name: 'Opened',
        width: 120,
        resizable: true,
        sortable: true,
      },
      
{
        key: 'ClosedDate',
        name: 'Closed',
        width: 120,
        resizable: true,
        sortable: true,
        frozen: false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ClosedDate || ''}
            type="date"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ClosedDate: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      
{
        key: 'ReasonClosed',
        name: 'Reason Closed',
        width: 150,
        resizable: true,
        sortable: true,
        frozen: false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ReasonClosed || ''}
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ReasonClosed: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'TotalHours',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Total Hours</b>
            {frozenColumns.indexOf('TotalHours') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalHours')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalHours')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'TotalHours')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'TotalHours')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 140,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.TotalHours}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    TotalHours: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('TotalHours') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.TotalHours}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                TotalHours: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
    ];

    if (orgLength.length > 1) {
      opportunity_performance_columns.splice(2, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      opportunity_performance_columns.splice(2, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = opportunity_performance_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = opportunity_performance_columns.filter(
      (col) => col.frozen === false,
    );
    opportunity_performance_columns = [...frozenCols, ...unfrozenCols];

    return opportunity_performance_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    opportunities,
    clients,
    companies,
    employees,
    organisations,
    orgLength,
    dates,
    handleSelectFrozenColumns,
  ]);
  
  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimOpportunityKey !== 'All'
          ? // eslint-disable-next-line
            r.DimOpportunityKey == filters.DimOpportunityKey
          : true) &&
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimEmployeeDirectoryKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeDirectoryKey == filters.DimEmployeeDirectoryKey
          : true) &&
        (filters.DimEmployeeProjectManagerKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectManagerKey ==
            filters.DimEmployeeProjectManagerKey
          : true) &&
        (filters.DimEmployeeSupervisorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeSupervisorKey == filters.DimEmployeeSupervisorKey
          : true) &&
        (filters.DimEmployeeOpportunitySourceOneKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeOpportunitySourceOneKey ==
            filters.DimEmployeeOpportunitySourceOneKey
          : true) &&
        (filters.DimEmployeeOpportunitySourceTwoKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeOpportunitySourceTwoKey ==
            filters.DimEmployeeOpportunitySourceTwoKey
          : true) &&
        (filters.DimClientKey !== 'All'
          ? // eslint-disable-next-line
            r.DimClientKey == filters.DimClientKey
          : true) &&
        (filters.TotalHours
          ? r.TotalHours?.toString().includes(filters.TotalHours.toString())
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    let export_value = {
      'Opportunity Performance': {
        columns: [],
        data: [],
      },
      Opportunity: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
      Client: {
        columns: [],
        data: [],
      },
      Employee: {
        columns: [],
        data: [],
      },
      'Opportunity Type': {
        columns: [],
        data: [],
      },
      'Opportunity Sub Type': {
        columns: [],
        data: [],
      },
    };

    export_value['Opportunity Performance'].columns = [
      { title: 'DimDateKey', width: { wpx: 90 } },
      { title: 'OpportunityId', width: { wpx: 110 } },
      { title: 'Name', width: { wpx: 180 } },
      { title: 'Client', width: { wpx: 120 } },
      { title: 'Director', width: { wpx: 120 } },
      { title: 'Project Manager', width: { wpx: 140 } },
      { title: 'Supervisor', width: { wpx: 120 } },
      { title: 'Source One', width: { wpx: 120 } },
      { title: 'Source Two', width: { wpx: 120 } },
      { title: 'Total Hours', width: { wpx: 90 } },
      { title: 'Status', width: { wpx: 110 } },
      { title: 'Revenue', width: { wpx: 100 } },
      { title: 'Probability Of Success', width: { wpx: 130 } },
      { title: 'Potential Revenue', width: { wpx: 120 } },
      { title: 'Weighted Potential Revenue', width: { wpx: 160 } },
      { title: 'Reason Closed', width: { wpx: 140 } },
      { title: 'Opportunity Type', width: { wpx: 120 } },
      { title: 'Opportunity Sub Type', width: { wpx: 140 } },
      { title: 'Opened Date', width: { wpx: 110 } },
      { title: 'Closed Date', width: { wpx: 110 } },
    ];
    exportData.forEach((data) => {
      export_value['Opportunity Performance'].data.push([
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : '',
          style: { numFmt: '0' },
        },
        { value: data.DimOpportunityKey ? getOpportunityIdById(data.DimOpportunityKey) : '' },
        { value: data.Name || (data.DimOpportunityKey ? getOpportunityNameById(data.DimOpportunityKey) : '') },
        { value: data.DimClientKey ? getClientNameById(data.DimClientKey) : '' },
        { value: data.DimEmployeeDirectoryKey ? getEmployeeNameById(data.DimEmployeeDirectoryKey) : '' },
        { value: data.DimEmployeeProjectManagerKey ? getEmployeeNameById(data.DimEmployeeProjectManagerKey) : '' },
        { value: data.DimEmployeeSupervisorKey ? getEmployeeNameById(data.DimEmployeeSupervisorKey) : '' },
        { value: data.DimEmployeeOpportunitySourceOneKey ? getEmployeeNameById(data.DimEmployeeOpportunitySourceOneKey) : '' },
        { value: data.DimEmployeeOpportunitySourceTwoKey ? getEmployeeNameById(data.DimEmployeeOpportunitySourceTwoKey) : '' },
        {
          value: data.TotalHours !== undefined && data.TotalHours !== null ? Number(data.TotalHours) : '',
          style: { numFmt: '0.00' },
        },
        { value: data.Status || '' },
        {
          value: data.Revenue !== undefined && data.Revenue !== null ? Number(data.Revenue) : '',
          style: { numFmt: '0.00' },
        },
        {
          value: data.ProbabilityOfSuccess !== undefined && data.ProbabilityOfSuccess !== null ? Number(data.ProbabilityOfSuccess) : '',
          style: { numFmt: '0.00' },
        },
        {
          value: data.PotentialRevenue !== undefined && data.PotentialRevenue !== null ? Number(data.PotentialRevenue) : '',
          style: { numFmt: '0.00' },
        },
        {
          value: data.WeightedPotentialRevenue !== undefined && data.WeightedPotentialRevenue !== null ? Number(data.WeightedPotentialRevenue) : '',
          style: { numFmt: '0.00' },
        },
        { value: data.ReasonClosed || '' },
        { value: data.OpportunityType ? getOpportunityTypeNameById(data.OpportunityType) : '' },
        { value: data.OpportunitySubType ? getOpportunitySubTypeNameById(data.OpportunitySubType) : '' },
        { value: data.OpenedDate || '' },
        { value: data.ClosedDate || '' },
      ]);
    });

    export_value.Opportunity.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    opportunities &&
      opportunities.forEach((cur) => {
        export_value.Opportunity.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    export_value.Client.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    clients &&
      clients.forEach((cur) => {
        export_value.Client.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value.Employee.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    employees &&
      employees.forEach((cur) => {
        export_value.Employee.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Opportunity Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 120 } },
    ];
    projecttypes &&
      projecttypes.forEach((cur) => {
        export_value['Opportunity Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Opportunity Sub Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 120 } },
    ];
    projectsubtypes &&
      projectsubtypes.forEach((cur) => {
        export_value['Opportunity Sub Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });
    return export_value;
  }, [
    exportData,
    opportunities,
    companies,
    organisations,
    dates,
    clients,
    employees,
    projecttypes,
    projectsubtypes,
  ]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <Button
          onClick={() => onClickedAdd(name, 'auto')}
          variant="outlined"
          color="success"
        >
          Auto Add
        </Button>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={opportunityPerformanceColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel
                filename="Opportunity Performance"
                value={exportValue}
              />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

OpportunityPerformanceTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  clients: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  projecttypes: PropTypes.array.isRequired,
  projectsubtypes: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getEmployees: PropTypes.func.isRequired,
  getProjectTypes: PropTypes.func.isRequired,
  getProjectSubTypes: PropTypes.func.isRequired,
};

export default OpportunityPerformanceTable;
